import { NavLink } from "react-router-dom";
import { Home, Users, UploadCloud, Shield, Sun, Moon, LogOut, Layers } from "lucide-react";
import { useProgramAccess } from "@/platform/ProgramAccessContext";
import { useProgramYear } from "@/contexts/ProgramYearContext";
import { useActiveProgram } from "@/platform/useActiveProgram";
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarHeader,
  SidebarFooter,
  useSidebar,
} from "@/components/ui/sidebar";
import VBCALogo from "@/components/shared/VBCALogo";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

export function AppSidebar() {
  const { state } = useSidebar();
  const collapsed = state === "collapsed";

  const [sidebarTheme, setSidebarTheme] = useState<'dark' | 'light'>(() => (localStorage.getItem('sidebarTheme') as 'dark' | 'light') || 'dark');
  useEffect(() => {
    localStorage.setItem('sidebarTheme', sidebarTheme);
  }, [sidebarTheme]);
  const isDark = sidebarTheme === 'dark';

  const { user: authUser, logout } = useAuth();
  const { grantedModules } = useProgramAccess();
  const { programYear } = useProgramYear();
  const activeProgram = useActiveProgram();
  const user = authUser || { name: "Test User", email: "test.user@example.com" };
  const initials = user.name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
  return (
    <Sidebar collapsible="icon" className={`${isDark ? "dark " : ""}${collapsed ? "w-14" : "w-64"} bg-sidebar border-r border-sidebar-border`}>
        <SidebarHeader className="h-14 px-3 border-b border-sidebar-border bg-sidebar flex items-center">
            <div className="flex items-center gap-3">
              <VBCALogo size={40} />
              {!collapsed && (
                <div className="leading-tight text-sidebar-foreground">
                  <div className="text-sm font-semibold">Value Based Care Advisors</div>
                  <div className="text-xs opacity-90">Programs Portal</div>
                </div>
              )}
            </div>
        </SidebarHeader>
        <SidebarContent className="sidebar-scroll">

        {/* Main */}
        <SidebarGroup>
          <SidebarGroupLabel className="mt-1.5 pt-2 border-t border-sidebar-border text-[0.8rem] font-semibold text-muted-foreground">Main</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink to="/">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Home className="mr-2 h-4 w-4" />
                      {!collapsed && <span>Home</span>}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Programs (P7.1): the sidebar shows ONLY the ACTIVE program's group.
            Program routes derive it from the path; elsewhere the session program
            is kept so Home / Operations do not flip to the Programs launcher
            (owner 2026-07-21). Launcher only when no program is selected. */}
        {(() => {
          const visibleModules = grantedModules.filter(m => m.supportedYears(programYear));
          const activeModule = visibleModules.find(m => m.code === activeProgram);

          if (activeModule) {
            return (
              <SidebarGroup key={activeModule.code}>
                <SidebarGroupLabel className="mt-1.5 pt-2 border-t border-sidebar-border text-[0.8rem] font-semibold text-muted-foreground">
                  {activeModule.navLabel}
                </SidebarGroupLabel>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {activeModule.navItems.map(item => (
                      <SidebarMenuItem key={item.to}>
                        <NavLink to={item.to} end={item.end}>
                          {({ isActive }) => (
                            <SidebarMenuButton isActive={isActive}>
                              <item.icon className="mr-2 h-4 w-4" />
                              {!collapsed && <span>{item.label}</span>}
                            </SidebarMenuButton>
                          )}
                        </NavLink>
                      </SidebarMenuItem>
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            );
          }

          // No program selected yet: one entry per granted program.
          return (
            <SidebarGroup>
              <SidebarGroupLabel className="mt-1.5 pt-2 border-t border-sidebar-border text-[0.8rem] font-semibold text-muted-foreground">
                Programs
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {visibleModules.map(m => (
                    <SidebarMenuItem key={m.code}>
                      <NavLink to={m.homeRoute}>
                        {() => (
                          <SidebarMenuButton>
                            <Layers className="mr-2 h-4 w-4" />
                            {!collapsed && <span>{m.displayName}</span>}
                          </SidebarMenuButton>
                        )}
                      </NavLink>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          );
        })()}

        {/* Operations */}
        <SidebarGroup>
          <SidebarGroupLabel className="mt-1.5 pt-2 border-t border-sidebar-border text-[0.8rem] font-semibold text-muted-foreground">Operations</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink to="/upload">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <UploadCloud className="mr-2 h-4 w-4" />
                      {!collapsed && <span>Data Management</span>}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavLink to="/patients">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Users className="mr-2 h-4 w-4" />
                      {!collapsed && <span>Patients</span>}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <NavLink to="/admin">
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive}>
                      <Shield className="mr-2 h-4 w-4" />
                      {!collapsed && <span>Admin</span>}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      <SidebarFooter className="mt-auto p-3 border-t border-sidebar-border bg-sidebar">
        {collapsed ? (
          <div className="px-1 py-1.5">
            <div className="flex items-center justify-center">
              <Avatar className="h-9 w-9 bg-sidebar-accent/40 ring-1 ring-inset ring-sidebar-border">
                <AvatarFallback className="text-sidebar-foreground font-semibold tracking-wide">{initials}</AvatarFallback>
              </Avatar>
            </div>
          </div>
        ) : (
          <div className="rounded-lg bg-sidebar-accent p-2.5 border border-sidebar-border text-sidebar-foreground">
            <div className="flex items-center gap-3">
              <Avatar className="h-8 w-8 bg-sidebar-accent/40 ring-1 ring-inset ring-sidebar-border">
                <AvatarFallback className="text-sidebar-foreground font-semibold tracking-wide">{initials}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <div className="text-sm font-medium leading-tight">{user.name}</div>
                <div className="text-xs text-muted-foreground truncate">{user.email}</div>
              </div>
              <div className="ml-auto flex items-center gap-1.5">
                <button
                  onClick={() => setSidebarTheme(isDark ? 'light' : 'dark')}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sidebar-border bg-sidebar hover:bg-sidebar-accent text-sidebar-foreground"
                  aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                >
                  {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                </button>
                <button
                  onClick={logout}
                  className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-sidebar-border bg-sidebar hover:bg-sidebar-accent text-sidebar-foreground"
                  aria-label="Logout"
                >
                  <LogOut className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        )}
      </SidebarFooter>
    </Sidebar>
  );
}

export function AppSidebarProvider({ children }: { children: React.ReactNode }) {
  return <SidebarProvider>{children}</SidebarProvider>;
}


